import React, { forwardRef, useImperativeHandle, useRef, useState } from "react";
import { RCCHookToast, RCCRefModalProps, RCCServicesApi } from "react-common-components";
import {  useForm } from "react-hook-form";
import Modal from "src/components/Modal";
import { useLoaderBackdrop } from "src/context/LoaderBackdrop/context";
import { useTranslation } from "src/context/Translations/context";
import { getValidationErrors, standardErrorMessages } from "src/utils/errors";
import { useConfirmModal } from "src/context/ConfirmModal/context";
import { v4 } from "uuid";
import { IComponentRunAuditModal, IRefModalRunAudit } from "./interface";
import { IAuditItems } from "../../interface";
import ItemAudited from "./itemAudited";

const ModalRunAudit : React.ForwardRefRenderFunction<IRefModalRunAudit,IComponentRunAuditModal> = ({
	onCallbackUpdate
}, ref) => {

	const { translate } = useTranslation();
	const { addToast } = RCCHookToast();
	const { setLoading } = useLoaderBackdrop();
	const {confirm} = useConfirmModal();
	const { handleSubmit, control, setError, setValue, register, reset, formState: { errors }, clearErrors } = useForm();

	const [selectedAuditId, setSelectedAuditId] = useState<number>();
	const [selectedAuditTitle, setSelectedAuditTitle] = useState<string>();
	const [selectedAuditItems, setSelectedAuditItems] = useState<IAuditItems[]>({} as IAuditItems[]);
	const modal = useRef<RCCRefModalProps>(null);
    
	useImperativeHandle(ref, () => ({
		openModal
	}));

	const openModal = (auditId: number, title: string) => {
		reset();
		clearErrors();
		setSelectedAuditId(auditId);
		setSelectedAuditTitle(title);
		queryAuditData(auditId);
	};

	const queryAuditData = async (id: number) => {
		setLoading(true);
		try{
			const {data} = await RCCServicesApi.getRequest({
				method: "Audits/SelectRun",
				urlParameters: {
					id
				}
			});

			setSelectedAuditItems(data);
			modal.current?.openModal();

			setLoading(false);
		}catch(err){
			setLoading(false);
			standardErrorMessages(err, translate("Erro ao carregar a auditoria"), addToast);
		}
	};

	const onSubmitConfirm = (data: any) => {
		confirm(
			translate("Executar Auditoria"), 
			translate("Deseja realmente executar a auditoria?"),
			async () => onSubmit(data),
			(err) => {
				standardErrorMessages(err, translate("Erro ao executar auditoria"), addToast);
			},
			false
		);
	};
	
	const onSubmit = async (data: any) => {
		clearErrors();
		try{
			modal.current?.setLoadingButton();
					
			const dataForm = {
				...data
			};

			if(data.adtbb004 && data.adtbb004.length > 0){
				dataForm.adtbb004 = data.adtbb004.map((adtbb004: any) => ({
					...adtbb004,
					adb004Adb001Id: String(selectedAuditId),
					adb004Situacao: adtbb004.adb004Situacao === "null" ? "" : adtbb004.adb004Situacao
				}));
			}

			const formData = new FormData();
			formData.append("Adb001Id", String(selectedAuditId));
			formData.append("Adb001Titulo", selectedAuditTitle || "");
			formData.append("Adtbb004", JSON.stringify(
				dataForm.adtbb004
			));

			data.adtbb004.forEach((item: any) => {
				if(item.attachments){
					item.attachments.forEach((attach: any) => {
						formData.append(`${item.adb004Id}`, attach);
					});
				}
			});

			await RCCServicesApi.postRequest({
				method: "Audits/Run",
				contentType: "multipart/form-data",
				body: formData
			});

			if(onCallbackUpdate)
				onCallbackUpdate(selectedAuditId);

			addToast({
				color: "green",
				description: translate("Auditoria executada com sucesso"),
				title: translate("Sucesso")
			});

			modal.current?.resetLoadingButton();
			modal.current?.closeModal();
		}catch(err: any){
			modal.current?.resetLoadingButton();

			if(getValidationErrors(err, setError)){
				addToast({
					title: translate("Erro"),
					description: translate("Verifique os erros no formulário e tente novamente"),
					color: "yellow"
				});
				return;
			}

			const textMessageError = err?.response?.data?.Message;
			if(textMessageError === "withoutCategoryNotConform"){
				addToast({
					title: translate("Atenção"),
					description: translate("Nenhuma Categoria de Não Conforme foi cadastrada"),
					color: "yellow"
				});
				return;
			}
			
			if(textMessageError === "withoutCategoryOpportunityImprovement"){
				addToast({
					title: translate("Atenção"),
					description: translate("Nenhuma Categoria de Oportunidade de Melhoria foi cadastrada"),
					color: "yellow"
				});
				return;
			}
			
			if(textMessageError === "withoutCategoryObservation"){
				addToast({
					title: translate("Atenção"),
					description: translate("Nenhuma Categoria de Observação foi cadastrada"),
					color: "yellow"
				});
				return;
			}
			
			standardErrorMessages(err, translate("Erro ao executar auditoria"), addToast);
		}
	};

	return (
		<>
			<Modal
				ref={modal} 
				header={{
					title: translate("Executar Auditoria"),
					icon: {
						name: "play"
					},
					subtitle: `${translate("Auditoria")}: ${selectedAuditTitle}`
				}}
				onSubmit={handleSubmit(onSubmitConfirm)}
				footer={{
					showSaveButton: true,
					buttonLoadingText: translate("Carregando"),
					saveButtonText: translate("Salvar"),
					cancelButtonText: translate("Cancelar")
				}}
			>
				{
					selectedAuditItems.length > 0 && selectedAuditItems.map((item: any, index) => (
						<ItemAudited 
							key={v4()}
							item={item}
							index={index}
							disabled={false}
							control={control}
							register={register}
							errors={errors}
							setValue={setValue}
							fetchAttachments={false}
						/>
					))
				}
			</Modal>
		</>
	);
};

export default forwardRef(ModalRunAudit);
